import { useLocale, useTranslate } from 'react-admin'

import type { ScaleWarning } from '@/data/source'
import { formatDateTime } from '@/domain/dateTime'

export const useWarningReason = () => {
  const translate = useTranslate()
  const locale = useLocale()
  const number = (value: number): string => value.toLocaleString(locale, { maximumFractionDigits: 0 })
  const duration = (minutes: number): string => {
    const rounded = Math.round(minutes)
    const hours = number(Math.floor(rounded / 60))
    if (rounded < 60) return translate('pages.scales.warnings.minutes', { minutes: number(rounded) })
    if (rounded % 60 === 0) return translate('pages.scales.warnings.whole_hours', { hours })
    return translate('pages.scales.warnings.hours', { hours, minutes: number(rounded % 60) })
  }
  return (warning: ScaleWarning): string => {
    switch (warning.kind) {
      case 'DURATION': {
        const { warning: details } = warning
        return details.reason === 'SHORT'
          ? translate('pages.scales.warnings.short', { duration: duration(details.minutes) })
          : translate('pages.scales.warnings.fast', { rate: number(details.tonnesPerHour), duration: duration(details.minutes) })
      }
      case 'IMPLAUSIBLE_QUANTITY':
        return translate('pages.scales.warnings.implausible_quantity', {
          goods: warning.goods ?? '—',
          quantity: warning.quantity === null ? '—' : warning.quantity.toLocaleString(locale),
          unit: warning.unit ?? '',
        }).trim()
      case 'OUTSIDE_STAY':
        return translate('pages.scales.warnings.outside_stay', {
          start: formatDateTime(warning.marker.startTime),
          end: warning.marker.endTime === null ? '…' : formatDateTime(warning.marker.endTime),
          stay_start: formatDateTime(warning.stay.start),
          stay_end: warning.stay.end === null ? '…' : formatDateTime(warning.stay.end),
        })
      case 'MISSING_PRODUCT':
        return translate('pages.scales.warnings.missing_product')
      case 'MISSING_TONNES':
        return translate('pages.scales.warnings.missing_tonnes')
    }
  }
}
